import type { ChangeType, ConfidenceBucket, IOrganizationPlan } from '../../../models/Plan';
import type { IScanResult } from '../../../models/ScanResult';
import type { IResolvedChange } from '../../../plan/PlanSimulator';
import { filePath } from '../../../rules/ruleUtils';

export const CHANGE_LABEL: { [type in ChangeType]: string } = {
  quarantineFile: 'Quarantine file',
  quarantineFolder: 'Quarantine folder',
  renameFile: 'Rename',
  mergeFolders: 'Consolidate folders',
  moveFolder: 'Move folder',
  flattenFolders: 'Flatten folders',
  fileIntake: 'File from intake',
  restructure: 'Move to new structure'
};

export const BUCKET_LABEL: { [bucket in ConfidenceBucket]: string } = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
  needsInput: 'Needs your input'
};

function join(parent: string, name: string): string {
  return parent ? `${parent}/${name}` : name;
}

/** Display name of the item a change acts on. */
export function changeTitle(scan: IScanResult, r: IResolvedChange): string {
  const c = r.change;
  return c.itemKind === 'file' ? scan.files[c.targetId].name : scan.folders[r.targetFolderId ?? c.targetId].name;
}

/** Where things are now. */
export function changeFrom(scan: IScanResult, r: IResolvedChange): string[] {
  const c = r.change;
  if (c.type === 'mergeFolders') {
    return (r.sourceFolderIds ?? []).map(id => scan.folders[id].path);
  }
  if (c.type === 'flattenFolders') {
    return [c.flattenFrom ?? scan.folders[c.targetId].path];
  }
  return [c.itemKind === 'file' ? filePath(scan.files[c.targetId]) : scan.folders[c.targetId].path];
}

/** Where things would end up, or undefined when the admin still has to decide. */
export function changeTo(scan: IScanResult, plan: IOrganizationPlan, r: IResolvedChange): string | undefined {
  const c = r.change;
  switch (c.type) {
    case 'quarantineFile':
      return join(plan.quarantinePath, filePath(scan.files[c.targetId]));
    case 'quarantineFolder':
      return join(plan.quarantinePath, scan.folders[c.targetId].path);
    case 'renameFile':
      return r.newName ? join(scan.files[c.targetId].parentPath, r.newName) : undefined;
    case 'moveFolder':
      return r.parentPath !== undefined ? join(r.parentPath, scan.folders[c.targetId].name) : undefined;
    case 'mergeFolders':
      return scan.folders[r.targetFolderId!].path;
    case 'flattenFolders':
      return c.flattenTo;
    case 'fileIntake':
      return r.parentPath ? join(r.parentPath, scan.files[c.targetId].name) : undefined;
    case 'restructure':
      return r.parentPath
        ? join(r.parentPath, c.itemKind === 'file' ? scan.files[c.targetId].name : scan.folders[c.targetId].name)
        : undefined;
  }
}
